/**
* @author      Shivani Mehra
* @date        OCt, 2025
* @copyright   www.ibirdsservices.com
*/

import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Card,
  Badge,
  Spinner,
  Row,
  Table,
  Dropdown,
} from 'react-bootstrap';
import SparkApi from '../../../api/SparkApi';
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from 'react-bs-datatable';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import CampaignEdit from './CampaignEdit';
import { ToastContainer, toast } from 'react-toastify'; // npm i react-toastify --force
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import fileDownload from 'js-file-download';
import helper from '../../common/helper';
import * as constants from '../../../constants/CONSTANT';
import Confirm from '../../Confirm';
import { ShimmerTable } from 'react-shimmer-effects';
import { FaTrash, FaEdit, FaPlus, FaClone } from 'react-icons/fa';
import DataApi from '../../../api/DataApi';

const Campaign = ({ selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [performanceCount, setPerformanceCount] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [modalShowHide, setModalShowHide] = useState(false);
  const [rowData, setRowData] = useState();
  const [showHideModel, setShowHideModel] = useState(false);
  const { campaign_name, sent, delivered, read, clicked, performance_score } =
    performanceCount;

  const bestPerformer = read !== '0';

  useEffect(() => {
    fetchData(selectedWhatsAppSetting);
    // fetchPerformance(selectedWhatsAppSetting);
  }, [selectedWhatsAppSetting]);

  useEffect(() => {
    const hasActiveCampaign = body.some(record => {
      const status = (
        record.campaign_status ||
        record.status ||
        ''
      ).toLowerCase();
      return status === 'pending' || status === 'in progress';
    });

    if (hasActiveCampaign) {
      const interval = setInterval(() => {
        fetchData(selectedWhatsAppSetting);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [body, selectedWhatsAppSetting]);

  const fetchData = async selectedWhatsAppSetting => {
    try {
      const dataApi = new DataApi('whatsapp');

      const result = await dataApi.fetchById(
        `campaign?whatsapp_setting_number=${selectedWhatsAppSetting}`
      );

      console.log('result', result.data);
      if (result.data.success) {
        const campaigns = result.data.data || [];

        const formattedResult = campaigns.map(record => {
          return {
            // Map new fields to old field names for compatibility
            campaign_id: record.campaign_id || record.id,
            campaign_name: record.campaign_name || record.name,
            template_name: record.template_name,
            start_date: record.schedule_date,
            campaign_status: record.campaign_status || record.status,
            id: record.campaign_id || record.id,
            name: record.campaign_name || record.name,
            status: record.campaign_status || record.status,
            // Keep original field structure
            total: 0,
            delivered: 0,
            read: 0,
            clicked: 0,
            response_rate: 0,
            file_title: record.file_data?.file_name,
            file_url: record.file_data?.file_url,
            // Include all original properties
            ...record,
          };
        });

        setBody(formattedResult);
      } else {
        setBody([]);
      }
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      setBody([]);
    } finally {
      setIsSpinner(true);
    }
  };
  // Create table headers consisting of 4 columns.
  const labels = { beforeSelect: ' ' };
  const header = [
    {
      title: 'Name',
      prop: 'campaign_name',
      isFilterable: true,
      cell: row => (
        <Link to={`/campaign/track/${row.campaign_id}`} state={row}>
          {row.campaign_name}
        </Link>
      ),
    },
    {
      title: 'Template Name',
      prop: 'template_name',
      isFilterable: true,
      cell: row => row.template_name?.replace(/_/g, ' ') || '-',
    },
    {
      title: 'Schedule Date & Time',
      prop: 'start_date',
      cell: row =>
        row.start_date
          ? moment(new Date(row.start_date)).format('DD-MM-YYYY h:mm:ss A')
          : '',
    },
    {
      title: 'Total',
      prop: 'total',
      isFilterable: true,
      cell: row => row.total ?? 0,
    },
    {
      title: 'Delivered',
      prop: 'delivered',
      isFilterable: true,
      cell: row => row.delivered ?? 0,
    },
    {
      title: 'Read',
      prop: 'read',
      isFilterable: true,
      cell: row => row.read ?? 0,
    },
    {
      title: 'Clicked',
      prop: 'clicked',
      isFilterable: true,
      cell: row => row.clicked ?? 0,
    },
    {
      title: 'Read Rate',
      prop: 'response_rate',
      isFilterable: true,
      cell: row => `${row.response_rate ?? 0}%`,
    },
    {
      title: 'Status',
      prop: 'campaign_status',
      isFilterable: true,
      cell: row => {
        const status = row.campaign_status?.toLowerCase() || 'pending';
        const statusStyles = {
          completed: 'rgb(199, 216, 241)',
          pending: 'rgb(241 218 177)',
          aborted: 'rgb(248 195 200)',
          'in progress': 'rgb(208 187 244)',
          default: 'rgb(199, 216, 241)',
        };
        const bgColor = statusStyles[status] || statusStyles.default;

        return (
          <Button
            className='btn-sm text-capitalize'
            variant=''
            style={{
              borderRadius: '15px',
              backgroundColor: bgColor,
              cursor: 'default',
            }}
          >
            {row.campaign_status || 'Pending'}
          </Button>
        );
      },
    },
    {
      title: 'Owner Name',
      prop: 'owner_name',
      isFilterable: true,
      cell: row => {
        const ownerName = row.owner_name || row.createdbyname || row.created_by_name || 'System';
        const ownerId = row.ownerid || row.createdbyid;
        if (ownerId && ownerName !== 'System') {
          return (
            <Link to={`/view/user/${ownerId}`} state={row}>
              {ownerName}
            </Link>
          );
        }
        return ownerName;
      },
    },
    {
      title: 'Actions',
      prop: 'id',
      cell: row => (
        <div className='d-inline-flex gap-1 align-items-center justify-content-start'>
          <Button
            size='sm'
            variant='outline-primary'
            className='d-inline-flex align-items-center gap-1 border-1 shadow-sm'
            onClick={() => cloneRecord({ row })}
            style={{
              borderRadius: '6px',
              fontWeight: '500',
              padding: '0.25rem 0.5rem',
            }}
          >
            <FaClone size={12} /> Clone
          </Button>
          {row.campaign_status?.toLowerCase() === 'pending' && (
            <Button
              size='sm'
              variant='outline-danger'
              className='d-inline-flex align-items-center gap-1 border-1 shadow-sm'
              onClick={() => deleteRecord({ row })}
              style={{
                borderRadius: '6px',
                fontWeight: '500',
                padding: '0.25rem 0.5rem',
              }}
            >
              <FaTrash size={12} /> Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const createCampaignRecord = () => {
    navigate('/campaign/add');
  };

  const cloneRecord = data => {
    const startDate = moment(data?.row.start_date);
    if (!startDate.isValid()) {
      data.row.start_date = moment().format('YYYY-MM-DD HH:mm:ss');
    }
    navigate(`/campaign/${data?.row.campaign_id}`, {
      state: { ...data?.row, type: 'clone' },
    });
  };

  const editRecord = data => {
    setModalShowHide(true);
    setRowData(data?.row);
  };

  const deleteRecord = data => {
    setShowHideModel(true);
    setRowData(data?.row);
  };

  const deleteCampaignRecord = async () => {
    if (rowData?.campaign_id) {
      const result = await SparkApi.deleteCampaignRecord(rowData.campaign_id);
      if (result.success) {
        toast.success('Record deleted successfully.');
        fetchData(selectedWhatsAppSetting);
        setShowHideModel(false);
      } else {
        setShowHideModel(false);
        toast.error(result.error.message);
      }
    }
  };

  const onRefreshData = () => {
    toast.success('Record updated successfully.');
    fetchData(selectedWhatsAppSetting);
    setModalShowHide(false);
  };

  const downloadFile = async row => {
    try {
      if (!row?.file_title) {
        toast.error('File name required.');
        return;
      }

      const result = await SparkApi.downloadCampaignFile(row?.file_title);

      if (result) {
        fileDownload(result, row?.file_title);
        toast.success('File download successfully.');
      } else {
        toast.warning('File not found.');
      }
    } catch (error) {
      // console.error('Error downloading file:', error);
      if (error.message === 'Unauthorized access. Please login.') {
        toast.error('Unauthorized access. Please login.');
      } else {
        toast.error('Failed to download file. Please try again.');
      }
    }
  };

  return (
    <>
      <Container className='mt-5 mb-5'>
        <Row className='mx-5 mb-4'>
          <Card className='p-4 bg-white shadow-sm border-0 rounded-3'>
            <h5 className='mb-2 fw-bold'>💡 Quick Guide</h5>
            <small>
              Start your WhatsApp campaign to send personalized messages,
              promote offers, and engage customers instantly—all in one place.
            </small>
          </Card>
        </Row>

        <Row className='mx-5 g-0'>
          <Col lg={12} className='mb-3'>
            <Row className='g-0'>
              <Col lg={12}>
                <div className='bg-white shadow-sm border-0 rounded-3 p-4'>
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <h5 className='fw-semibold mb-0'>📦 All Campaigns</h5>
                    <Button
                      className='btn btn-sm d-inline-flex align-items-center gap-2'
                      variant='primary'
                      onClick={() => createCampaignRecord()}
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: '6px',
                        fontWeight: '500',
                      }}
                    >
                      <FaPlus /> Add New Campaign
                    </Button>
                  </div>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className='mb-3 align-items-center'>
                        <Col
                          lg={6}
                          md={6}
                          sm={12}
                          className='mb-2 mb-md-0'
                        >
                          <Filter />
                        </Col>
                        <Col
                          lg={6}
                          md={6}
                          sm={12}
                          className='d-flex justify-content-md-end'
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                      </Row>

                      <div className='table-responsive'>
                        <Table
                          striped
                          bordered
                          className='related-list-table'
                          responsive='sm'
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {modalShowHide && (
        <CampaignEdit
          show={modalShowHide}
          onHide={() => {
            setModalShowHide(false);
          }}
          onRefreshData={onRefreshData}
          rowData={rowData}
          table='campaign'
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}
      {showHideModel && (
        <Confirm
          show={showHideModel}
          onHide={() => setShowHideModel(false)}
          deleteResponseRecord={deleteCampaignRecord}
          title='Confirm delete?'
          message='You are going to delete the record. Are you sure?'
          table='response_message'
        />
      )}

      <ToastContainer />
    </>
  );
};

export default Campaign;
